import { formatDistanceToNow, format, parseISO } from 'date-fns'
import { es } from 'date-fns/locale'

/**
 * Convierte una fecha UTC (string ISO o sin sufijo Z) a Date local
 * La API puede devolver fechas UTC sin el sufijo Z, por lo que lo añadimos si no está presente
 */
export const parseUtcDate = (utcDate) => {
  if (!utcDate) return null
  try {
    let dateStr = utcDate
    // Si la fecha no termina en Z y no tiene timezone, añadir Z para indicar UTC
    if (typeof dateStr === 'string' && !dateStr.endsWith('Z') && !dateStr.includes('+') && !dateStr.includes('-', 10)) {
      dateStr = dateStr + 'Z'
    }
    return parseISO(dateStr)
  } catch (error) {
    console.error('Error parseando fecha UTC:', error)
    return null
  }
}

/**
 * Formatea una fecha ISO a "hace X tiempo"
 * Si la fecha contiene 'Utc' en su nombre original (pasar isUtc=true), la convierte primero a hora local
 */
export const formatRelativeTime = (isoDate, isUtc = false) => {
  if (!isoDate) return ''
  try {
    let date
    if (isUtc) {
      date = parseUtcDate(isoDate)
    } else {
      date = typeof isoDate === 'string' ? parseISO(isoDate) : isoDate
    }
    if (!date) return ''
    return formatDistanceToNow(date, { addSuffix: true, locale: es })
  } catch (error) {
    console.error('Error formateando fecha:', error)
    return ''
  }
}

/**
 * Formatea una fecha ISO a formato legible
 * Si la fecha contiene 'Utc' en su nombre original (pasar isUtc=true), la convierte primero a hora local
 */
export const formatDate = (isoDate, formatStr = 'dd/MM/yyyy HH:mm', isUtc = false) => {
  if (!isoDate) return ''
  try {
    let date
    if (isUtc) {
      date = parseUtcDate(isoDate)
    } else {
      date = typeof isoDate === 'string' ? parseISO(isoDate) : isoDate
    }
    if (!date) return ''
    return format(date, formatStr, { locale: es })
  } catch (error) {
    console.error('Error formateando fecha:', error)
    return ''
  }
}

/**
 * Formatea bytes a tamaño legible
 */
export const formatBytes = (bytes, decimals = 2) => {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const dm = decimals < 0 ? 0 : decimals
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i]
}

/**
 * Trunca un texto a una longitud máxima
 */
export const truncate = (str, maxLength = 50) => {
  if (!str) return ''
  if (str.length <= maxLength) return str
  return str.substring(0, maxLength) + '...'
}

/**
 * Capitaliza la primera letra de un string
 */
export const capitalize = (str) => {
  if (!str) return ''
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
}

/**
 * Calcula el tiempo transcurrido entre dos fechas en formato legible (Xh Ym Zs)
 * Si solo se pasa la fecha inicial, calcula hasta ahora
 */
export const formatDuration = (startDate, endDate = new Date()) => {
  if (!startDate) return ''

  try {
    const start = typeof startDate === 'string' ? parseISO(startDate) : startDate
    const end = typeof endDate === 'string' ? parseISO(endDate) : endDate

    const diffMs = end - start
    if (diffMs < 0) return '0s'

    const seconds = Math.floor(diffMs / 1000)
    const minutes = Math.floor(seconds / 60)
    const hours = Math.floor(minutes / 60)
    const days = Math.floor(hours / 24)

    const remainingHours = hours % 24
    const remainingMinutes = minutes % 60
    const remainingSeconds = seconds % 60

    const parts = []
    if (days > 0) parts.push(`${days}d`)
    if (remainingHours > 0) parts.push(`${remainingHours}h`)
    if (remainingMinutes > 0) parts.push(`${remainingMinutes}m`)
    if (remainingSeconds > 0 && days === 0) parts.push(`${remainingSeconds}s`)

    return parts.length > 0 ? parts.join(' ') : '0s'
  } catch (error) {
    console.error('Error calculando duración:', error)
    return ''
  }
}
